import type { DateTime } from "luxon";
import { matchesResponseFilter, RequestFilter } from "@/components/RequestData/RequestsTab/utils";
import { slotWindowOnDay } from "@/utils/dateUtils";
import type { BaseRequest, HospitalData, Job, Nurse, NurseInShift, OpenShiftRequest, ScheduleShift } from "@/utils/models";
import { requestTakesAnyRole, storedFormsOfRoles } from "@/utils/roles";
import { toIsoDate } from "@/utils/scheduleWeeks";
import type { GapsScopedShiftGroup } from "../gapsGroupSheets";

// What the Open Shifts screen reads off a posted request and the people who answered it. Nothing here decides a
// number the rest of the app has not already decided: each rule is an existing one, named once so the list, the
// approve sheet and the posted sheet describe a request identically.

/** The response switch's options, in the order the gaps layout lists them. */
export const GAPS_RESPONSE_FILTERS: readonly RequestFilter[] = [
	RequestFilter.All,
	RequestFilter.WithResponse,
	RequestFilter.WithoutResponse,
	RequestFilter.ReplaceRequest,
];

/** How many requests each switch option would list — counted with the switch's own test (`matchesResponseFilter`). */
export const countByResponseFilter = (requests: readonly BaseRequest[]): Record<RequestFilter, number> => ({
	[RequestFilter.All]: requests.length,
	[RequestFilter.WithResponse]: requests.filter((request) => matchesResponseFilter(request, RequestFilter.WithResponse)).length,
	[RequestFilter.WithoutResponse]: requests.filter((request) => matchesResponseFilter(request, RequestFilter.WithoutResponse)).length,
	[RequestFilter.ReplaceRequest]: requests.filter((request) => matchesResponseFilter(request, RequestFilter.ReplaceRequest)).length,
});

/** A person's hours on a request: what they accepted for a partial pickup, else the posted shift's. */
export type GapsAcceptedHours = { start: DateTime; end: DateTime };

/**
 * Every shift the loaded weeks hold on schedule day `date`, across every shift period. `null` when that day isn't
 * loaded: the phone does not hold it, which is NOT the same as a day with nobody on it.
 */
export const shiftsOnScheduleDay = (weeks: HospitalData["weeks"], date: DateTime): NurseInShift[] | null => {
	const isoDate = toIsoDate(date);
	const week = weeks.find((candidate) => candidate.weeklyShifts.some((shift) => shift.days.some((day) => toIsoDate(day.date) === isoDate)));
	if (week === undefined) return null;
	// DEFAULT: a shift period that skips the day has nobody on it.
	return week.weeklyShifts.flatMap((shift) => shift.days.find((day) => toIsoDate(day.date) === isoDate)?.nurses ?? []);
};

/** Another shift an applicant holds that day, as far as the phone can say — it may not hold the day at all. */
export type GapsOtherShiftThatDay = { isKnown: false } | { isKnown: true; shift: ScheduleShift | undefined };

/**
 * The applicant's other shift that day, if any — the line's "double shift" (P3-12). A shift already queued for removal
 * is on its way out, so it is not the second half of a double. Unknown for a day the phone has not loaded (`null`):
 * the confirm says "not known" rather than "no other shift". The day's shifts come from the old list
 * (`shiftsOnScheduleDay`) or, with the schedule cache on, the cached day.
 */
export const otherShiftThatDay = (dayShifts: readonly ScheduleShift[] | null, nurseId: number): GapsOtherShiftThatDay =>
	dayShifts === null ? { isKnown: false } : { isKnown: true, shift: dayShifts.find((shift) => shift.nurseId === nurseId && !shift.isPendingToDelete) };

/**
 * The job an applicant's chip names (the decision: "chip shows the JOB, not the role"): the job the request is for when
 * they hold it — the job they would be approved into — else their first job. `undefined` for someone with no job
 * (a department without job management), whose chip falls back to their role, the only label there is.
 */
export const applicantJob = (nurse: Pick<Nurse, "jobs">, requestJobId: string | undefined): Job | undefined =>
	// DEFAULT: someone who does not hold the request's job (a role-posted request, or a job they were since taken off)
	// is still labelled by a job of theirs — their first, the one the roster lists them under — rather than by none.
	nurse.jobs?.find((job) => job.id === requestJobId) ?? nurse.jobs?.[0];

/** Every shift a nurse holds across the loaded weeks — what an approval is diffed against to find the one it created. */
export const shiftsOfNurse = (weeks: HospitalData["weeks"], nurseId: number): NurseInShift[] =>
	weeks.flatMap((week) => week.weeklyShifts.flatMap((shift) => shift.days.flatMap((day) => day.nurses.filter((nurse) => nurse.nurseId === nurseId))));

/**
 * The shift an approval just created, for the assign-unit sheet that follows it (P3-6): a shift of the approved nurse
 * that did not exist before the write, preferring the one carrying the request's id (`prepareDataForAddNurse` stamps
 * it), else the one on the approved hours. `undefined` when the approval created nothing locally — a swap moves
 * existing shifts, and a dismissed or failed approval writes nothing.
 */
export const findShiftCreatedByApproval = ({
	before,
	after,
	requestId,
	hours,
}: {
	before: ReadonlySet<string>;
	after: readonly NurseInShift[];
	requestId: string;
	hours: GapsAcceptedHours;
}): NurseInShift | undefined => {
	const created = after.filter((shift) => !before.has(shift.id));
	return (
		created.find((shift) => shift.requestId === requestId) ??
		created.find((shift) => shift.startTime.toMillis() === hours.start.toMillis() && shift.endTime.toMillis() === hours.end.toMillis())
	);
};

/**
 * Whether a group's section is the one a request is for: its job for a job section, else its roles — read as the
 * Requests screen reads them ("all" takes every role; either stored role form counts).
 */
const groupTakesRequest = (group: GapsScopedShiftGroup, request: OpenShiftRequest): boolean =>
	group.scope.jobId !== null ? group.scope.jobId === request.job_id : requestTakesAnyRole(request.message.roles, storedFormsOfRoles(group.scope.roles));

const overlapMillis = (a: { startTime: DateTime; endTime: DateTime }, b: { start: DateTime; end: DateTime }): number =>
	Math.max(0, Math.min(a.endTime.toMillis(), b.end.toMillis()) - Math.max(a.startTime.toMillis(), b.start.toMillis()));

/**
 * The group a posted request is drawn under on the day, for "Add someone directly" (P3-10) — which opens the add sheet
 * LINKED to the request (part 2's missing-row path, P3-8). The group only frames the sheet (its badge, its heading);
 * the add itself takes the REQUEST's hours and the request's own unit, since no hole was tapped.
 *
 * First the group whose hole the desktop's matcher placed the request in (`postedRequestId` on a missing row). A request
 * the matcher placed nowhere (every hole it could fill is already taken, or it asks for more people than there are
 * holes) still has a section and hours: the group of the same job (or roles) whose window overlaps its hours the most.
 * `undefined` when no group of the day takes it at all, and the caller asks which shift instead.
 */
export const groupForLinkedRequest = (
	dayGroups: readonly GapsScopedShiftGroup[],
	request: OpenShiftRequest,
	date: DateTime,
	dayStart: string,
): GapsScopedShiftGroup | undefined => {
	const placed = dayGroups.find((group) => group.missing.some((entry) => entry.postedRequestId === request.id));
	if (placed !== undefined) return placed;
	const hours = { start: request.message.shift_start, end: request.message.shift_end };
	let best: { group: GapsScopedShiftGroup; overlap: number } | undefined;
	for (const group of dayGroups) {
		if (!groupTakesRequest(group, request)) continue;
		const overlap = overlapMillis(slotWindowOnDay(group, date, dayStart), hours);
		if (overlap > 0 && (best === undefined || overlap > best.overlap)) best = { group, overlap };
	}
	return best?.group;
};

/**
 * The unit an add that completes a request puts the person in, when no hole was tapped: the request's own, or none —
 * the assign-unit sheet then follows the add. A request posted to "any unit" carries `null` (or nothing) for it.
 */
export const unitOfRequest = (request: Pick<OpenShiftRequest, "location">): string | null =>
	typeof request.location === "string" && request.location !== "" ? request.location : null;
